import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlignLeft } from 'lucide-react'
import { Card } from '@/components/ui/card'
import type { ScheduleStats } from '../../schedule/types'

interface Props {
  stats: ScheduleStats
  projectId: string
}

// No `success`/`warning` Tailwind tokens exist in this project (only CSS vars meant for
// buttons/alerts) — reuse the raw palette classes `badgeVariants` uses for status tinting.
const PARTS: { key: keyof ScheduleStats; health: string; cls: string }[] = [
  { key: 'onTrack', health: 'ON_TRACK', cls: 'bg-green-500' },
  { key: 'atRisk', health: 'AT_RISK', cls: 'bg-amber-500' },
  { key: 'delayed', health: 'DELAYED', cls: 'bg-red-500' },
  { key: 'completed', health: 'COMPLETED', cls: 'bg-muted-foreground/40' },
]

/** Dashboard widget summarising all non-archived schedules in the project. Spec B §4. */
const ScheduleHealthCard: React.FC<Props> = ({ stats, projectId }) => {
  const { t } = useTranslation()
  const total = Math.max(1, stats.total)

  return (
    <Link to={`/project/${projectId}/schedule`}>
      <Card className="p-4 hover:bg-muted/50 transition-colors cursor-pointer">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-3xl font-bold leading-none">{stats.total}</span>
            <h4 className="text-sm font-medium text-foreground mt-1">{t('Schedule_dashboard_title')}</h4>
          </div>
          <AlignLeft className="h-8 w-8 text-muted-foreground/20" />
        </div>

        <div className="mt-3 flex h-2 w-full overflow-hidden rounded-full bg-muted">
          {PARTS.map(({ key, cls }) => {
            const n = stats[key] as number
            if (n <= 0) return null
            return <div key={key} className={cls} style={{ width: `${(n / total) * 100}%` }} /> // ui-drift: dynamic
          })}
        </div>

        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {PARTS.map(({ key, health, cls }) => (
            <span key={key} className="inline-flex items-center gap-1">
              <span className={`inline-block h-2 w-2 rounded-full ${cls}`} />
              {t(`Schedule_health_${health}`)} {stats[key] as number}
            </span>
          ))}
        </div>

        <p className="mt-2 text-xs text-muted-foreground">{t('Schedule_dashboard_critical', { count: stats.critical })}</p>
      </Card>
    </Link>
  )
}
export default ScheduleHealthCard
